<script setup lang="ts">
import ControlValidation from "@/components/control-validation.vue";
import { useControlValidation } from "@/composables/use-control-validation";
import type { UserDTO } from "@/models/dtos/user-dto";
import type { UpdateUserRequest } from "@/models/requests/update-user-request";
import { getUpdateUserSchema } from "@/schemas/update-user-schema";
import { authService } from "@/services/auth-service";
import { notificationsService } from "@/services/notifications-service";
import { usersService } from "@/services/users-service";
import { onBeforeMount, ref } from "vue";

function getDefaultFormData(user?: UserDTO): UpdateUserRequest {
  return {
    name: user?.name ?? "",
    email: user?.email ?? "",
    phone: user?.phone ?? "",
  };
}

const pageLoading = ref(false);
const loading = ref(false);
const user = ref<UserDTO | undefined>();
const users = ref<UserDTO[]>([]);
const formData = ref(getDefaultFormData());

const { result, validate } = useControlValidation(() => getUpdateUserSchema());

onBeforeMount(async () => {
  pageLoading.value = true;

  user.value = authService.loggedInUser;
  formData.value = getDefaultFormData(user.value);

  pageLoading.value = false;
});

async function onUpdate() {
  loading.value = true;

  try {
    const userId = user.value?.id;
    const valid = validate(formData.value);
    if (!valid || !userId) return;

    const updated = await usersService.updateUser(userId, formData.value);
    user.value = updated;
    formData.value = getDefaultFormData(user.value);
    notificationsService.open({
      text: "Profil uppdaterad.",
      type: "success",
    });
  } catch (error) {
    notificationsService.open({
      text: "Något gick fel vid uppdatering av profilen.",
      type: "error",
      error,
    });
  } finally {
    loading.value = false;
  }
}
</script>

<template>
  <section class="section px-0">
    <p class="title is-4">Profil</p>

    <app-loader v-if="pageLoading"></app-loader>

    <div v-else-if="user" class="columns">
      <div class="column is-6">
        <form @submit.prevent="onUpdate">
          <div class="field">
            <label class="label" for="name">Namn</label>
            <control-validation
              :result="result"
              field="name"
              #="{ inputClass }"
            >
              <div class="control">
                <input
                  id="name"
                  v-model="formData.name"
                  class="input"
                  type="text"
                  name="name"
                  :class="inputClass"
                />
              </div>
            </control-validation>
          </div>

          <div class="field">
            <label class="label" for="phone">Telefonummer</label>
            <control-validation
              :result="result"
              field="phone"
              #="{ inputClass }"
            >
              <div class="control">
                <input
                  id="phone"
                  v-model="formData.phone"
                  class="input"
                  type="tel"
                  name="phone"
                  :class="inputClass"
                />
              </div>
            </control-validation>
          </div>

          <div class="field">
            <label class="label" for="email">E-post</label>
            <control-validation
              :result="result"
              field="email"
              #="{ inputClass }"
            >
              <div class="control">
                <input
                  id="email"
                  v-model="formData.email"
                  class="input"
                  type="email"
                  name="email"
                  readonly
                  disabled
                  :class="inputClass"
                />
              </div>
            </control-validation>
          </div>

          <div class="control">
            <button class="button is-primary" type="submit">Uppdatera</button>
          </div>
        </form>
      </div>
    </div>
  </section>
</template>
